import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../../hooks/useTranslation';
import './Notification.css';

// Centralized notification state
let notificationState = {
  isVisible: false,
  message: '',
  type: 'success',
  link: null,
  setNotification: null,
};

// Centralized functions to control notifications.
// `message` accepts either:
//  - a plain string (shown as-is — used for legacy sites and backend-provided
//    error strings, which the backend localizes via Accept-Language), or
//  - { key: 'notifications.module.slug', params: {...} } — translated at
//    render time, so an on-screen toast re-renders on language switch.
// `options`: { duration?: ms (0 = sticky), action?: { label, onClick } }.
// An action renders as a button inside the toast (e.g. Undo) and the toast
// stays a little longer so it can be pressed.
let toastSeq = 0;
export const showNotification = (message, type = 'success', link = null, options = {}) => {
  if (notificationState.setNotification) {
    notificationState.setNotification({
      isVisible: true,
      message,
      type,
      link,
      action: options.action || null,
      duration: options.duration,
      seq: ++toastSeq,
    });
  }
};

// Undo toast: "Ticket archived  [Undo]". Resolves the caller's onUndo when
// pressed; the toast then closes. Sticks around for 6s.
export const showUndoToast = (message, onUndo, options = {}) =>
  showNotification(message, options.type || 'success', null, {
    duration: options.duration ?? 6000,
    action: { label: { key: 'common.undo' }, onClick: onUndo },
  });

// Coerce any message shape into readable text. The DRF exception handler wraps
// errors as { error, detail, status } where `detail` may itself be a string,
// an object ({ detail: "..." }), or field errors ({ name: ["..."] }). Rendering
// such an object directly throws "Objects are not valid as a React child", so
// flatten everything to a string here.
const messageToText = (m) => {
  if (m == null) return '';
  if (typeof m === 'string') return m;
  if (Array.isArray(m)) return m.map(messageToText).filter(Boolean).join(', ');
  if (typeof m === 'object') {
    if (m.detail !== undefined) return messageToText(m.detail);
    if (m.message !== undefined) return messageToText(m.message);
    return Object.values(m).map(messageToText).filter(Boolean).join(' ');
  }
  return String(m);
};

// Translation-key messages ({ key, params }) resolve through t(); everything
// else flattens to text. Kept as a function of t so it re-evaluates per render.
const resolveMessage = (m, t) => {
  if (m && typeof m === 'object' && !Array.isArray(m) && typeof m.key === 'string') {
    return t(m.key, undefined, m.params || {});
  }
  return messageToText(m);
};

export const hideNotification = () => {
  if (notificationState.setNotification) {
    notificationState.setNotification((prev) => ({ ...prev, isVisible: false }));
  }
};

const Notification = ({ duration = 3000 }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notification, setNotification] = useState({
    isVisible: false,
    message: '',
    type: 'success',
    link: null,
  });

  // Register this instance. Notification is mounted once globally (routes)
  // and, historically, again inside some pages. Keep a stack so the most
  // recently mounted instance shows toasts, and when it unmounts control
  // returns to the one below it instead of going dead.
  useEffect(() => {
    notificationState.stack = notificationState.stack || [];
    notificationState.stack.push(setNotification);
    notificationState.setNotification = setNotification;
    return () => {
      notificationState.stack = notificationState.stack.filter((s) => s !== setNotification);
      notificationState.setNotification = notificationState.stack[notificationState.stack.length - 1] || null;
    };
  }, []);

  // Handle auto-dismiss after duration (per-toast override, then the prop).
  // Keyed on `seq` so a second toast restarts the timer.
  const effective = notification.duration ?? (notification.action ? 6000 : duration);
  useEffect(() => {
    if (notification.isVisible && effective > 0) {
      const timer = setTimeout(() => {
        hideNotification();
      }, effective);
      return () => clearTimeout(timer);
    }
  }, [notification.isVisible, notification.seq, effective]);

  if (!notification.isVisible) return null;

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <FaCheckCircle className="notification-icon success" />;
      case 'error':
        return <FaExclamationCircle className="notification-icon error" />;
      default:
        return <FaCheckCircle className="notification-icon success" />;
    }
  };

  const handleClick = () => {
    if (notification.link) {
      navigate(notification.link);
      hideNotification();
    }
  };

  const action = notification.action;
  const runAction = (e) => {
    e.stopPropagation();
    hideNotification();
    action?.onClick?.();
  };

  return (
    <div
      role={notification.type === 'error' ? 'alert' : 'status'}
      aria-live={notification.type === 'error' ? 'assertive' : 'polite'}
      className={`notification ${notification.type} ${notification.isVisible ? 'show' : ''} ${notification.link ? 'clickable' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-content">
        {getIcon()}
        <span className="notification-message">{resolveMessage(notification.message, t)}</span>
        {action && (
          <button type="button" className="notification-action" onClick={runAction}>
            {resolveMessage(action.label, t)}
          </button>
        )}
        <button
          type="button"
          className="notification-dismiss"
          onClick={(e) => { e.stopPropagation(); hideNotification(); }}
          aria-label={t('common.dismiss')}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default Notification;